import React from "react"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { SecretInput } from "../SecretInput"
import { JsonTextareaField } from "./JsonTextareaField"
import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import type { WebSocketFieldProps } from "./types"

interface HttpFirstWebSocketFieldsProps extends WebSocketFieldProps {
  idPrefix?: "http-first" | "twiml"
  showInitializationJson?: boolean
}

export const HttpFirstWebSocketFields: React.FC<
  HttpFirstWebSocketFieldsProps
> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
  idPrefix = "http-first",
  showInitializationJson = true,
}) => {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">
        WebSocket Connection
      </h4>

      {/* Authorization Header (Optional) */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor={`${idPrefix}-auth-header`}
          tooltip={WEBSOCKET_TOOLTIPS.authorizationHeader}
        >
          Authorization Header
        </FieldLabel>
        <SecretInput
          id={`${idPrefix}-auth-header`}
          data-testid={`websocket-${idPrefix}-auth-header`}
          aria-label="Authorization Header"
          revealLabel="authorization header"
          placeholder="Bearer your-token-here"
          value={metadata.authorization_header || ""}
          onChange={(e) =>
            onMetadataUpdate({ authorization_header: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Authorization header value sent during the WebSocket handshake.
        </p>
      </div>

      {showInitializationJson && (
        <JsonTextareaField
          id={`${idPrefix}-init-json`}
          label="Initialization JSON (Optional)"
          ariaLabel="Initialization JSON"
          placeholder={'{\n  "type": "session_init",\n  "config": {}\n}'}
          description="Optional JSON object sent after the WebSocket connection is established. No message is sent if left empty (recommended when the session was already created via HTTP)."
          value={metadata.initialization_json}
          fieldName="initialization_json"
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
          tooltip={WEBSOCKET_TOOLTIPS.initializationJson}
        />
      )}

      {/* Custom Headers (Optional) */}
      <JsonTextareaField
        id={`${idPrefix}-custom-headers`}
        label="Custom Headers"
        ariaLabel="Custom Headers"
        placeholder={'{\n  "X-Coval-Simulation-Id": "{{simulation_id}}"\n}'}
        description="Additional HTTP headers as a JSON object. Sent during the WebSocket upgrade request."
        value={metadata.custom_headers}
        fieldName="custom_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={3}
        tooltip={WEBSOCKET_TOOLTIPS.customHeaders}
      />
    </div>
  )
}
